import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavigationHeader } from '../../../components/UploadNavHeader';
import { fetchEtlStatus } from '../../../utils/api';
import { useAppDispatch, useAppSelector } from '../../../state';
import { later } from '../../../utils/later';
import {
  setPublishStatus,
  selectFilesState,
} from '../../../state/slices/filesSlice';
import { StatusMessage, Wrapper } from './NavigationWrapper.styles';

interface INavigationWrapper {
  onFileUploadClick?: () => void;
  onTemplateDownloadClick?: () => void;
  isUploadDisabled?: boolean;
  showDownloadTemplate?: boolean;
  hideStatusMessage?: boolean;
}

const PUBLISH_POLLING_INTERVAL = 5000;

const DASHBOARD = 'DASHBOARD';
const NEW_MAPPING = 'NEW MAPPING';
const COMPLETED_MAPPINGS = 'COMPLETED MAPPINGS';
const REPORTS = 'REPORTS';

const TABS = [DASHBOARD, NEW_MAPPING, COMPLETED_MAPPINGS, REPORTS];

const ROUTES_MAP = {
  [DASHBOARD]: '/data-manager/dashboard',
  [NEW_MAPPING]: '/data-manager/upload-mapping',
  [COMPLETED_MAPPINGS]: '/data-manager/completed-mappings',
  [REPORTS]: '/data-manager/reports',
};

const NavigationWrapperComponent: React.FC<INavigationWrapper> = ({
  onFileUploadClick,
  isUploadDisabled,
  hideStatusMessage,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { publishStatus } = useAppSelector(selectFilesState);

  const [selectedTab, setSelectedTab] = useState<string>(
    Object.keys(ROUTES_MAP).find((tabName) =>
      location.pathname.startsWith(
        ROUTES_MAP[tabName as keyof typeof ROUTES_MAP]
      )
    ) || DASHBOARD
  );

  const handleOnTabClick = (tab: string) => {
    if (selectedTab !== tab) {
      setSelectedTab(tab);
      navigate(ROUTES_MAP[tab as keyof typeof ROUTES_MAP]);
    }
  };

  const updateStatus = async () => {
    try {
      const { status } = await fetchEtlStatus();

      dispatch(setPublishStatus(status));
    } catch {}
  };

  useEffect(() => {
    let stopTick = false;

    const tick = async () => {
      if (stopTick) {
        return;
      }

      await updateStatus();
      await later(PUBLISH_POLLING_INTERVAL);

      tick();
    };

    tick();
    return () => {
      stopTick = true;
    };
  }, []);

  const isPublishing =
    publishStatus === 'Queued' || publishStatus === 'Started';

  return (
    <NavigationHeader
      tabs={TABS}
      selectedTab={selectedTab}
      onTabClick={handleOnTabClick}
      disabledTabs={[]}
      secondaryContent={
        <Wrapper>
          {isPublishing && !hideStatusMessage && (
            <StatusMessage>
              Publishing is in progress. Please do not make any changes until
              publishing has completed
            </StatusMessage>
          )}
        </Wrapper>
      }
    />
  );
};

export const NavigationWrapper = React.memo(NavigationWrapperComponent);
